import { Badge, Button, Card, CardSection, ColorField, EmptyState, Field, Input, UploadField } from "@/components/ui";
import { slugify } from "@/lib/utils/format";
import type { Participant } from "@/lib/types/config";
import { useBuilder } from "../BuilderContext";
import { IconBadgeCheck, IconPlus, IconTrash } from "../components/icons";

/**
 * "Acts" — the participants a guest aligns with (teams, artists, stages).
 *
 * A participant crossed with a Vibe produces a Scenario, which is what actually
 * carries the prompt. Renaming a slug therefore orphans its scenarios, so slugs
 * are edited deliberately rather than derived live from the name.
 */
export function ActsTab() {
  const { draft, update } = useBuilder();
  const participants = draft.participants ?? [];
  const scenarios = draft.scenarios ?? [];

  function setParticipants(next: Participant[]) {
    update((current) => ({ ...current, participants: next }));
  }

  function add() {
    const stamp = Date.now();
    setParticipants([
      ...participants,
      { slug: `participant-${stamp}`, name: "New Participant", color: "#DFFF4F" },
    ]);
  }

  function patch(index: number, patchValue: Partial<Participant>) {
    setParticipants(participants.map((item, i) => (i === index ? { ...item, ...patchValue } : item)));
  }

  function remove(index: number) {
    const target = participants[index];
    setParticipants(participants.filter((_, i) => i !== index));
    // Drop the scenarios that referenced it, or publish fails cross-reference
    // validation with an error pointing at a participant that no longer exists.
    update((current) => ({
      ...current,
      scenarios: (current.scenarios ?? []).filter((s) => s.participant_slug !== target?.slug),
    }));
  }

  return (
    <CardSection
      eyebrow="Who"
      title="Acts"
      description="Artist, team, or stage choices guests can align with. Skip this entirely by turning on “Skip participants selection” in General."
      actions={<Button variant="outline" size="sm" icon={<IconPlus size={14} />} onClick={add}>Add Participant</Button>}
    >
      {participants.length === 0 ? (
        <EmptyState
          icon={<IconBadgeCheck size={28} />}
          title="No acts yet"
          description="Add participants when guests should pick a side. For a single-brand booth, leave this empty and let Vibes drive the prompts."
          action={<Button variant="primary" size="sm" onClick={add}>Add the first participant</Button>}
        />
      ) : (
        <div className="flex flex-col gap-4">
          {participants.map((participant, index) => {
            const usage = scenarios.filter((s) => s.participant_slug === participant.slug).length;

            return (
              <Card key={`${participant.slug}-${index}`} className="flex flex-col gap-5 p-5">
                <header className="flex items-center gap-3">
                  <span
                    className="h-9 w-9 shrink-0 rounded-lg border border-ink-500"
                    style={{ backgroundColor: participant.color }}
                    aria-hidden
                  />
                  <div className="flex min-w-0 flex-col leading-tight">
                    <span className="truncate text-sm font-semibold">{participant.name || "Untitled"}</span>
                    <span className="truncate font-mono text-2xs text-haze-400">{participant.slug}</span>
                  </div>
                  <div className="ml-auto flex items-center gap-2">
                    {usage > 0 && <Badge tone="aqua">{usage} scenario{usage === 1 ? "" : "s"}</Badge>}
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label={`Remove ${participant.name}`}
                      onClick={() => remove(index)}
                      className="text-haze-400 hover:text-danger"
                    >
                      <IconTrash size={14} />
                    </Button>
                  </div>
                </header>

                <div className="grid gap-5 sm:grid-cols-3">
                  <Field label="Slug" hint={usage > 0 ? "Used by scenarios — renaming rewires them." : undefined}>
                    <Input
                      className="font-mono"
                      value={participant.slug}
                      onChange={(e) => {
                        const nextSlug = slugify(e.target.value);
                        const previous = participant.slug;
                        patch(index, { slug: nextSlug });
                        // Keep scenarios pointing at this participant.
                        update((current) => ({
                          ...current,
                          scenarios: (current.scenarios ?? []).map((s) =>
                            s.participant_slug === previous ? { ...s, participant_slug: nextSlug } : s,
                          ),
                        }));
                      }}
                    />
                  </Field>

                  <Field label="Name">
                    <Input value={participant.name} onChange={(e) => patch(index, { name: e.target.value })} />
                  </Field>

                  <ColorField label="Color" value={participant.color} onChange={(value) => patch(index, { color: value })} />
                </div>

                <UploadField
                  label="Logo / crest"
                  value={participant.logo_path}
                  accept="image/*,video/mp4,video/webm,video/ogg"
                  onChange={(value) => patch(index, { logo_path: value, config: undefined })}
                  onUpload={(asset) => patch(index, {
                    logo_path: asset.signed_id,
                    config: { media_type: asset.content_type.startsWith("video/") ? "video" : "image" },
                  })}
                  hint="Upload the character image or a short looping character video shown on the selection card."
                />
              </Card>
            );
          })}
        </div>
      )}
    </CardSection>
  );
}
